import { parseISO } from '@blockworks/platform/libs/date-fns';

import type { ArticleType } from '@/types/article-type';
import decodeEntities from '@/utils/functions/string/decode-entities';
import stripHtmlTags from '@/utils/functions/string/strip-html-tags';
import mapYoastMeta from '@/utils/functions/wordpress/map-yoast-meta';

interface OGImage {
    width?: number;
    height?: number;
    url: string;
    type: string;
}

const mapWpPost = (r: any): ArticleType => {
    const featuredMedia =
        // eslint-disable-next-line no-underscore-dangle
        r?._embedded && r?._embedded?.['wp:featuredmedia'] ? r?._embedded?.['wp:featuredmedia'][0] : null;
    const terms =
        // eslint-disable-next-line no-underscore-dangle
        r?._embedded && r?._embedded?.['wp:term']
            ? // eslint-disable-next-line no-underscore-dangle
              r?._embedded?.['wp:term']
            : null;

    const categories = terms ? terms[0]?.map((c: any) => c.name) || [] : [];
    const tags = terms
        ? terms[1]?.map((t: any) => ({
              name: t.name,
              slug: t.slug,
          })) || []
        : [];
    const excerpt = r.excerpt?.rendered ?? r.excerpt;
    const { slug, content } = r;

    let urlPath;
    switch (r.type) {
        case 'webinars':
            urlPath = 'webinar';
            // We changed /webinar to /roundtable on May 14th, 2024 (URL is singular for slugs)
            r.yoast_head_json.canonical = r.yoast_head_json.canonical?.replace(`/webinars/`, `/roundtable/`);
            break;
        case 'events':
            urlPath = 'event';
            r.yoast_head_json.canonical = r.yoast_head_json.canonical?.replace(`/events/`, `/event/`);
            break;
        default:
            urlPath = 'news';
            break;
    }

    const permalink = `/${urlPath}/${slug}`;

    let meta = mapYoastMeta(r.yoast_head_json);
    if (Array.isArray(r.yoast_head_json?.og_image)) {
        const ogImages: OGImage[] = [];
        // TODO: need to figure out why WpAPI returns wrong image url, this is temp workaround.
        r.yoast_head_json.og_image.forEach((img: OGImage) => {
            ogImages.push({
                ...img,
                url: img.url.replace('cms.cms.blockworks.co', 'cms.blockworks.co'),
            });
        });

        meta = { ...meta, og_image: ogImages };
    }

    return {
        slug,
        id: r.id,
        permalink,
        title: decodeEntities(r.title?.rendered || ''),
        type: r.type,
        typeLabel: r.type,
        isSticky: r.sticky ?? false,
        imageUrl: featuredMedia?.source_url || '',
        thumbnail: {
            url: featuredMedia?.source_url || '',
            caption: decodeEntities((featuredMedia?.caption?.rendered ?? '').replace('<br />', ' ')),
            dimensions: {
                width: featuredMedia?.media_details?.width || 0,
                height: featuredMedia?.media_details?.height || 0,
            },
        },
        lastUpdate: parseISO(`${r.modified_gmt}Z`).toUTCString() || '',
        publishedAt: parseISO(`${r.date_gmt}Z`).toUTCString() || '',
        publishedAtTimestamp: r.date_gmt ? parseISO(`${r.date_gmt}Z`).getTime() / 1000 : 0,
        excerpt: decodeEntities(stripHtmlTags(excerpt ?? '')),
        content: typeof content === 'object' ? content.rendered : `${content}`,
        primaryCategory: categories.length > 0 ? categories[0] : '',
        categories,
        primaryTag: tags.length > 0 ? tags[0] : null,
        tags,
        customFields: r.acf || null,
        meta,
        authors: r.authors
            ? r.authors
                  .filter((author: any) => author.display_name && author.user_id)
                  .map((author: any) => ({
                      title: author.display_name || '',
                      id: author.user_id || '',
                      slug: author.slug || '',
                      image_url: author.image_url || '',
                  }))
            : [],
        blurhash: null,
    };
};

export default mapWpPost;
